/* eslint max-lines: ["error", { "max": 1500 }] */
import {
  EmployeePaymentMethodType,
  EmployeesReportColumn,
  EmployeesReportDistinctValuesDto,
  EmployeesReportFilter,
  EmployeesReportRowDto,
  EmployeesReportView,
  EmployeesReportViewCountsDto,
  GenerateEmployeesReportDto,
  GetEmployerDto,
  Meta,
  OpenEnrollmentPeriodDto,
  SortDirection,
} from '@zorro/clients';
import { Avatar, getInitials } from '@zorro/design-system/v2/Avatar';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { singleLineTruncatedTextWithEllipsis } from '@zorro/shared/formatters';
import { Group, LoadingOverlay, Text } from '@zorro/web/design-system';
import {
  hasIncompleteQleElection,
  useImpersonation,
} from '@zorro/web/ui-utils';
import { DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useMemo, useState } from 'react';

import { AgentStatusBadge } from '../AgentStatusBadge';
import { useAnalytics } from '../Analytics';
import {
  AUTO_PAY_STATUS_SELECT_OPTIONS,
  BENEFIT_ELIGIBILITY_SELECT_OPTIONS,
  EMPLOYEE_ROLE_SELECT_OPTIONS,
  EMPLOYMENT_TYPE_SELECT_OPTIONS,
  ENROLLMENT_STATUS_SELECT_OPTIONS,
  FAMILY_UNIT_SELECT_OPTIONS,
  INITIAL_PREMIUM_STATUS_SELECT_OPTIONS,
  NULL_SELECT_OPTION,
  ONBOARDING_TYPE_SELECT_OPTIONS,
  PAYMENT_METHOD_TYPE_SELECT_OPTIONS,
  PLAN_MARKET_SELECT_OPTIONS,
  PLAN_TYPE_SELECT_OPTIONS,
  QLE_TYPE_SELECT_OPTIONS,
  SELF_ENROLL_SELECT_OPTIONS,
  SUBMISSION_TYPE_SELECT_OPTIONS,
  USER_STATUS_SELECT_OPTIONS,
  YES_NO_SELECT_OPTIONS,
  mapDatatableFilters,
} from '../DatatableUtils';
import { DynamicOverflowCell } from '../DynamicOverflowCell';
import { EligibilityStatusBadgeTooltip } from '../EligibilityStatusBadge';
import {
  ActionsMenu,
  EmployeeActionsOverlays,
  RecordForActions,
} from '../EmployeeActions';
import { EmployeeManagementActionsMenu } from '../EmployeeManagementActions';
import { EnrollmentStatusBadge } from '../EnrollmentStatus';
import { Filter, SelectFilter } from '../Filters';
import { PaginationSelect } from '../PaginationSelect';
import {
  AutoPayStatusBadge,
  InitialPaymentStatusBadge,
  PaymentMethodBadge,
} from '../Payments';
import { PendingElection } from '../PendingElection';
import { PlanMarketBadgeTableView } from '../PlanMarketBadgePageView';
import { PlanYearTitle } from '../PlanYearTitle';
import { QualifyingLifeEventLabel } from '../QualifyingLifeEventLabel';
import { SearchableDataTable } from '../SearchableDataTable';
import { TableTextCell } from '../TableTextCell';
import { EmployeeSsnCell } from './EmployeeSsnCell';
import {
  AllowanceModelPendingEmptyState,
  NoEmployeesEmptyState,
} from './EmployeesEmptyStates';
import { EmployeesDatatableStoredParams } from './employeesDatatable.types';
import {
  downloadEmployees,
  getAvailableViews,
  getEmployeeDatatableLink,
  getEmployeeReportColumnDisplayValue,
  getEmployeeReportColumnTitle,
} from './employeesDatatable.utils';
import { defaultQueryParams } from './usePaginatedEmployeeReports';

function renderCellValue(
  column: EmployeesReportColumn,
  record: EmployeesReportRowDto
) {
  const displayValue = getEmployeeReportColumnDisplayValue(column, record);
  return <Text size="sm">{displayValue ?? '-'}</Text>;
}

function renderTextCellValue(
  column: EmployeesReportColumn,
  record: EmployeesReportRowDto,
  widthThreshold?: number
) {
  const displayValue = getEmployeeReportColumnDisplayValue(column, record);
  return (
    <TableTextCell
      value={displayValue ?? null}
      widthThreshold={widthThreshold}
    />
  );
}

function getColumnTitleForFilter(column: EmployeesReportColumn): string {
  const title = getEmployeeReportColumnTitle(column);
  return `${title}:`;
}

type Props = {
  employer: GetEmployerDto;
  selectedEnrollmentPeriod: OpenEnrollmentPeriodDto | null;
  pageRecords: EmployeesReportRowDto[];
  counts?: EmployeesReportViewCountsDto;
  filterOptions?: EmployeesReportDistinctValuesDto[];
  isLoading: boolean;
  meta?: Meta;
  employeesDatatableParams: EmployeesDatatableStoredParams;
  setEmployeesDatatableParams: (
    value:
      | EmployeesDatatableStoredParams
      | ((
          prevState: EmployeesDatatableStoredParams
        ) => EmployeesDatatableStoredParams)
  ) => void;
  hiddenColumns: EmployeesReportColumn[];
  isSelectable?: boolean;
  selectedEmployees?: EmployeesReportRowDto[];
  setSelectedEmployeeIds?: (employees: string[]) => void;
  employeesForActions: RecordForActions[];
  setOverlayId: (overlayId: EmployeeActionsOverlays) => void;
  setOpenEnrollmentPeriodQuery: (periodId: string) => Promise<URLSearchParams>;
  setEnrollmentPeriodLocal: (periodId: string) => void;
  isDon?: boolean;
  hasAllowanceModel?: boolean;
};

export const EmployeesDatatable = ({
  employer,
  selectedEnrollmentPeriod,
  pageRecords,
  counts,
  filterOptions,
  isLoading,
  meta,
  employeesDatatableParams,
  setEmployeesDatatableParams,
  hiddenColumns = [],
  isSelectable,
  selectedEmployees,
  setSelectedEmployeeIds,
  employeesForActions,
  setOverlayId,
  setOpenEnrollmentPeriodQuery,
  setEnrollmentPeriodLocal,
  isDon = false,
  hasAllowanceModel = true,
}: Props) => {
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const { sendEvent } = useAnalytics();

  const { employee: impersonatedEmployee, isImpersonated } = useImpersonation();
  const impersonatedEmployeeId = isImpersonated
    ? impersonatedEmployee?.id
    : undefined;

  function shouldHideColumn(column: EmployeesReportColumn) {
    return hiddenColumns.includes(column);
  }

  const {
    view,
    sortStatus,
    filters,
    searchValue,
    page,
    pageSize = defaultQueryParams.pageSize,
  } = employeesDatatableParams;

  const availableViews = getAvailableViews(selectedEnrollmentPeriod, counts);

  useEffect(() => {
    const isCurrentViewAvailable = availableViews.some(
      ({ value }) => view === value
    );
    if (!isCurrentViewAvailable) {
      setEmployeesDatatableParams((prev: EmployeesDatatableStoredParams) => ({
        ...prev,
        view: EmployeesReportView.ALL,
        page: 1,
      }));
    }
  }, [availableViews, view, setEmployeesDatatableParams]);

  const getFilterOptionsForColumn = (column: EmployeesReportColumn) => {
    if (!filterOptions) return [];

    const columnValues = filterOptions.find(
      (option) => option.column === column
    );
    if (!columnValues) return [];

    switch (column) {
      case EmployeesReportColumn.enrollmentAgentName:
        return (
          columnValues.nameEmailValues?.map((value) => ({
            value: String(value.email),
            label: value.email ? `${value.name} (${value.email})` : '-',
          })) ?? []
        );

      case EmployeesReportColumn.enrollmentTeamName:
        return (
          columnValues.idNameValues?.map((value) => ({
            value: String(value.id),
            label: value.id ? String(value.name) : '-',
          })) ?? []
        );

      case EmployeesReportColumn.dependentCount:
        return (
          columnValues.numberValues?.map((value) => ({
            value: String(value.value),
            label: value.value ? String(value.value) : '-',
          })) ?? []
        );

      default:
        return (
          columnValues.stringValues?.map((value) => ({
            value: String(value.value),
            label: value.value ? String(value.value) : '-',
          })) ?? []
        );
    }
  };

  const handleFilterChange = (value: Filter<EmployeesReportRowDto>[]) => {
    if (value.length > 0) {
      void sendEvent('filter_column', {
        column_name: value[0].type,
        filter_criteria: value[0].value,
      });
    }
    setEmployeesDatatableParams((prev: EmployeesDatatableStoredParams) => ({
      ...prev,
      filters: value,
      page: 1,
    }));
    setSelectedEmployeeIds?.([]);
  };

  const handleClearFilters = () => {
    setEmployeesDatatableParams((prev: EmployeesDatatableStoredParams) => ({
      ...prev,
      filters: [],
      page: 1,
    }));
  };

  const handleSearchValueChange = (value: string) => {
    setEmployeesDatatableParams((prev: EmployeesDatatableStoredParams) => ({
      ...prev,
      searchValue: value,
      page: 1,
    }));
    setSelectedEmployeeIds?.([]);
  };

  const handleSortStatusChange = (value: DataTableSortStatus) => {
    setEmployeesDatatableParams((prev: EmployeesDatatableStoredParams) => ({
      ...prev,
      sortStatus: value,
      page: 1,
    }));
    void sendEvent('sort_column', {
      column_name: value.columnAccessor,
      sort_direction: value.direction,
    });
  };

  const handlePageChange = (newPage: number) => {
    void sendEvent('page_navigation', {
      navigation_type: newPage - page > 0 ? 'next' : 'previous',
    });
    setEmployeesDatatableParams((prev: EmployeesDatatableStoredParams) => ({
      ...prev,
      page: newPage,
    }));
    setSelectedEmployeeIds?.([]);
  };

  const handlePageSizeChange = (value?: string | null) => {
    void sendEvent('select_per_page', {
      from: pageSize,
      to: value,
    });
    if (value) {
      setSelectedEmployeeIds?.([]);
      setEmployeesDatatableParams((prev: EmployeesDatatableStoredParams) => ({
        ...prev,
        pageSize: parseInt(value),
        page: 1,
      }));
    }
  };

  const handleCurrentView = (view: string) => {
    setEmployeesDatatableParams((prev: EmployeesDatatableStoredParams) => ({
      ...prev,
      view: view as EmployeesReportView,
      page: 1,
    }));
  };

  const handleDownload = async () => {
    void sendEvent('click_download_table', {
      row_count: meta?.total,
    });

    const filterBy = mapDatatableFilters<
      EmployeesReportColumn,
      EmployeesReportRowDto,
      EmployeesReportFilter
    >(filters);
    const generateEmployeesReportDto: GenerateEmployeesReportDto = {
      view,
      sortBy: sortStatus.columnAccessor as EmployeesReportColumn,
      sortDirection: sortStatus.direction as SortDirection,
      filterBy,
      searchTerm: searchValue,
      perPage: 500,
      page: 1,
    };

    setIsDownloadLoading(true);
    try {
      await downloadEmployees(
        employer.name,
        employer.id,
        selectedEnrollmentPeriod?.id || '',
        generateEmployeesReportDto,
        hiddenColumns
      );
      void sendEvent('employees_report_downloaded', {
        employerId: employer.id,
        rowCount: meta?.total,
      });
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: EmployeesReportColumn.employeeFullName,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.employeeFullName
        ),
        sortable: true,
        width: '240px',
        hidden: shouldHideColumn(EmployeesReportColumn.employeeFullName),
        render: ({
          onboardingPeriodId = '',
          ...employee
        }: EmployeesReportRowDto) => (
          <DynamicOverflowCell
            value={`${employee.employeeFirstName} ${employee.employeeLastName}`}
            target="_blank"
            leftSection={
              <Avatar size="sm" color="indigo">
                {getInitials(
                  employee.employeeFirstName,
                  employee.employeeLastName
                )}
              </Avatar>
            }
            href={getEmployeeDatatableLink({
              onboardingPeriodId,
              impersonatedEmployeeId,
              employeeId: employee.employeeId,
              employerId: employer.id,
              isDon,
            })}
          />
        ),
      },
      {
        accessor: EmployeesReportColumn.email,
        title: getEmployeeReportColumnTitle(EmployeesReportColumn.email),
        sortable: true,
        width: '15rem',
        hidden: shouldHideColumn(EmployeesReportColumn.email),
        render: (record: EmployeesReportRowDto) =>
          renderTextCellValue(EmployeesReportColumn.email, record),
      },
      {
        accessor: EmployeesReportColumn.invitationStatus,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.invitationStatus
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.invitationStatus
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.invitationStatus
              )}
              selectOptions={USER_STATUS_SELECT_OPTIONS}
              filterBy={EmployeesReportColumn.invitationStatus}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.invitationStatus),
        render: ({ invitationStatus }: EmployeesReportRowDto) => (
          <AgentStatusBadge status={invitationStatus} />
        ),
      },
      {
        accessor: EmployeesReportColumn.benefitEligibility,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.benefitEligibility
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.benefitEligibility
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.benefitEligibility
              )}
              selectOptions={[
                ...BENEFIT_ELIGIBILITY_SELECT_OPTIONS,
                NULL_SELECT_OPTION,
              ]}
              filterBy={EmployeesReportColumn.benefitEligibility}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        width: '18rem',
        hidden: shouldHideColumn(EmployeesReportColumn.benefitEligibility),
        render: ({
          benefitEligibility,
          employeeEligibleFrom,
          employeeEligibleUntil,
          leaveOfAbsenceStartDate,
          leaveOfAbsenceEndDate,
          employeeCreatedAt,
          employeeTerminationDate,
          hasPreviousEligibility,
        }: EmployeesReportRowDto) => {
          return benefitEligibility ? (
            <EligibilityStatusBadgeTooltip
              employmentStatus={benefitEligibility}
              eligibleFrom={employeeEligibleFrom}
              eligibleUntil={employeeEligibleUntil}
              leaveOfAbsenceStartDate={leaveOfAbsenceStartDate}
              leaveOfAbsenceEndDate={leaveOfAbsenceEndDate}
              createdAt={employeeCreatedAt}
              terminationDate={employeeTerminationDate}
              hasPreviousEligibility={hasPreviousEligibility}
            />
          ) : (
            '-'
          );
        },
      },
      {
        accessor: EmployeesReportColumn.enrollmentStatus,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.enrollmentStatus
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.enrollmentStatus
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.enrollmentStatus
              )}
              selectOptions={ENROLLMENT_STATUS_SELECT_OPTIONS}
              filterBy={EmployeesReportColumn.enrollmentStatus}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.enrollmentStatus),
        render: ({ enrollmentStatus }: EmployeesReportRowDto) => (
          <EnrollmentStatusBadge enrollmentStatus={enrollmentStatus} />
        ),
      },
      {
        accessor: EmployeesReportColumn.class,
        title: getEmployeeReportColumnTitle(EmployeesReportColumn.class),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.class
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(EmployeesReportColumn.class)}
              selectOptions={getFilterOptionsForColumn(
                EmployeesReportColumn.class
              )}
              setFilters={handleFilterChange}
              filters={filters}
              filterBy={EmployeesReportColumn.class}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.class),
        render: (record: EmployeesReportRowDto) =>
          renderTextCellValue(EmployeesReportColumn.class, record),
      },
      {
        accessor: EmployeesReportColumn.dateOfBirth,
        title: getEmployeeReportColumnTitle(EmployeesReportColumn.dateOfBirth),
        sortable: true,
        hidden: shouldHideColumn(EmployeesReportColumn.dateOfBirth),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.dateOfBirth, record),
      },
      {
        accessor: EmployeesReportColumn.onboardingPeriodAllowance,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.onboardingPeriodAllowance
        ),
        sortable: true,
        hidden: shouldHideColumn(
          EmployeesReportColumn.onboardingPeriodAllowance
        ),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(
            EmployeesReportColumn.onboardingPeriodAllowance,
            record
          ),
      },
      {
        accessor: EmployeesReportColumn.enrollmentType,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.enrollmentType
        ),
        sortable: true,
        width: '9.5rem',
        cellsStyle: () => ({ paddingLeft: '1rem', paddingRight: '1rem' }),
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.enrollmentType
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.enrollmentType
              )}
              selectOptions={[
                ...ONBOARDING_TYPE_SELECT_OPTIONS,
                NULL_SELECT_OPTION,
              ]}
              filterBy={EmployeesReportColumn.enrollmentType}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.enrollmentType),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.enrollmentType, record),
      },
      {
        accessor: EmployeesReportColumn.onboardingFrom,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.onboardingFrom
        ),
        sortable: true,
        cellsStyle: () => ({
          paddingLeft: '1rem',
          paddingRight: '1.5rem',
        }),
        hidden: shouldHideColumn(EmployeesReportColumn.onboardingFrom),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.onboardingFrom, record),
      },
      {
        accessor: EmployeesReportColumn.onboardingUntil,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.onboardingUntil
        ),
        sortable: true,
        cellsStyle: () => ({
          paddingLeft: '1rem',
          paddingRight: '1.5rem',
        }),
        hidden: shouldHideColumn(EmployeesReportColumn.onboardingUntil),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.onboardingUntil, record),
      },
      {
        accessor: EmployeesReportColumn.enrollmentSubmissionDate,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.enrollmentSubmissionDate
        ),
        sortable: true,
        hidden: shouldHideColumn(
          EmployeesReportColumn.enrollmentSubmissionDate
        ),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(
            EmployeesReportColumn.enrollmentSubmissionDate,
            record
          ),
      },
      {
        accessor: EmployeesReportColumn.enrollmentSubmissionType,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.enrollmentSubmissionType
        ),
        sortable: true,
        filtering: filters.some(
          (filter) =>
            filter.type === EmployeesReportColumn.enrollmentSubmissionType
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.enrollmentSubmissionType
              )}
              selectOptions={[
                ...SUBMISSION_TYPE_SELECT_OPTIONS,
                NULL_SELECT_OPTION,
              ]}
              filterBy={EmployeesReportColumn.enrollmentSubmissionType}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(
          EmployeesReportColumn.enrollmentSubmissionType
        ),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(
            EmployeesReportColumn.enrollmentSubmissionType,
            record
          ),
      },
      {
        accessor: EmployeesReportColumn.enrollmentEffectiveFrom,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.enrollmentEffectiveFrom
        ),
        sortable: true,
        width: '10.5rem',
        hidden: shouldHideColumn(EmployeesReportColumn.enrollmentEffectiveFrom),
        render: (record: EmployeesReportRowDto) =>
          hasIncompleteQleElection(record.enrollmentStatus, record.qleType) ? (
            <PendingElection />
          ) : (
            renderCellValue(
              EmployeesReportColumn.enrollmentEffectiveFrom,
              record
            )
          ),
      },
      {
        accessor: EmployeesReportColumn.enrollmentEffectiveUntil,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.enrollmentEffectiveUntil
        ),
        sortable: true,
        hidden: shouldHideColumn(
          EmployeesReportColumn.enrollmentEffectiveUntil
        ),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(
            EmployeesReportColumn.enrollmentEffectiveUntil,
            record
          ),
      },
      {
        accessor: EmployeesReportColumn.benefitFamilyUnit,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.benefitFamilyUnit
        ),
        cellsStyle: () => ({ padding: '0 1rem' }),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.benefitFamilyUnit
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.benefitFamilyUnit
              )}
              selectOptions={[
                ...FAMILY_UNIT_SELECT_OPTIONS,
                NULL_SELECT_OPTION,
              ]}
              filterBy={EmployeesReportColumn.benefitFamilyUnit}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.benefitFamilyUnit),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.benefitFamilyUnit, record),
      },
      {
        accessor: EmployeesReportColumn.dependentCount,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.dependentCount
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.dependentCount
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.dependentCount
              )}
              selectOptions={getFilterOptionsForColumn(
                EmployeesReportColumn.dependentCount
              )}
              filterBy={EmployeesReportColumn.dependentCount}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.dependentCount),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.dependentCount, record),
      },
      {
        accessor: EmployeesReportColumn.medicalCarrier,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.medicalCarrier
        ),
        width: '240px',
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.medicalCarrier
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.medicalCarrier
              )}
              selectOptions={getFilterOptionsForColumn(
                EmployeesReportColumn.medicalCarrier
              )}
              filterBy={EmployeesReportColumn.medicalCarrier}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.medicalCarrier),
        render: (record: EmployeesReportRowDto) =>
          renderTextCellValue(EmployeesReportColumn.medicalCarrier, record),
      },
      {
        accessor: EmployeesReportColumn.medicalPlanName,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.medicalPlanName
        ),
        width: '240px',
        cellsStyle: () => ({ padding: '0 1rem' }),
        sortable: true,
        hidden: shouldHideColumn(EmployeesReportColumn.medicalPlanName),
        render: (record: EmployeesReportRowDto) =>
          renderTextCellValue(EmployeesReportColumn.medicalPlanName, record),
      },
      {
        accessor: EmployeesReportColumn.initialPremiumPaymentStatus,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.initialPremiumPaymentStatus
        ),
        sortable: true,
        filtering: filters.some(
          (filter) =>
            filter.type === EmployeesReportColumn.initialPremiumPaymentStatus
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.initialPremiumPaymentStatus
              )}
              selectOptions={[
                ...INITIAL_PREMIUM_STATUS_SELECT_OPTIONS,
                NULL_SELECT_OPTION,
              ]}
              filterBy={EmployeesReportColumn.initialPremiumPaymentStatus}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(
          EmployeesReportColumn.initialPremiumPaymentStatus
        ),
        render: ({ initialPremiumPaymentStatus }: EmployeesReportRowDto) =>
          initialPremiumPaymentStatus ? (
            <InitialPaymentStatusBadge status={initialPremiumPaymentStatus} />
          ) : (
            '-'
          ),
      },
      {
        accessor: EmployeesReportColumn.autoPayStatus,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.autoPayStatus
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.autoPayStatus
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.autoPayStatus
              )}
              selectOptions={[
                ...AUTO_PAY_STATUS_SELECT_OPTIONS,
                NULL_SELECT_OPTION,
              ]}
              filterBy={EmployeesReportColumn.autoPayStatus}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.autoPayStatus),
        render: ({ autoPayStatus }: EmployeesReportRowDto) =>
          autoPayStatus ? <AutoPayStatusBadge status={autoPayStatus} /> : '-',
      },
      {
        accessor: EmployeesReportColumn.medicalPlanType,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.medicalPlanType
        ),
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.medicalPlanType
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.medicalPlanType
              )}
              selectOptions={[...PLAN_TYPE_SELECT_OPTIONS, NULL_SELECT_OPTION]}
              filterBy={EmployeesReportColumn.medicalPlanType}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.medicalPlanType),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.medicalPlanType, record),
      },
      {
        accessor: EmployeesReportColumn.medicalPlanMarket,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.medicalPlanMarket
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.medicalPlanMarket
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.medicalPlanMarket
              )}
              selectOptions={[
                ...PLAN_MARKET_SELECT_OPTIONS,
                NULL_SELECT_OPTION,
              ]}
              filterBy={EmployeesReportColumn.medicalPlanMarket}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.medicalPlanMarket),
        render: ({
          medicalPlanMarket,
          onboardingPeriodIsWaived,
        }: EmployeesReportRowDto) => {
          return medicalPlanMarket && !onboardingPeriodIsWaived ? (
            <PlanMarketBadgeTableView planMarket={medicalPlanMarket} />
          ) : (
            '-'
          );
        },
      },
      {
        accessor: EmployeesReportColumn.medicalPlanId,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.medicalPlanId
        ),
        width: '240px',
        sortable: true,
        hidden: shouldHideColumn(EmployeesReportColumn.medicalPlanId),
        render: (record: EmployeesReportRowDto) =>
          renderTextCellValue(EmployeesReportColumn.medicalPlanId, record),
      },
      {
        accessor: EmployeesReportColumn.medicalPremium,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.medicalPremium
        ),
        sortable: true,
        hidden: shouldHideColumn(EmployeesReportColumn.medicalPremium),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.medicalPremium, record),
      },
      {
        accessor: EmployeesReportColumn.medicalEmployerContribution,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.medicalEmployerContribution
        ),
        sortable: true,
        hidden: shouldHideColumn(
          EmployeesReportColumn.medicalEmployerContribution
        ),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(
            EmployeesReportColumn.medicalEmployerContribution,
            record
          ),
      },
      {
        accessor: EmployeesReportColumn.medicalEmployeeContribution,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.medicalEmployeeContribution
        ),
        sortable: true,
        hidden: shouldHideColumn(
          EmployeesReportColumn.medicalEmployeeContribution
        ),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(
            EmployeesReportColumn.medicalEmployeeContribution,
            record
          ),
      },
      {
        accessor: EmployeesReportColumn.medicalIsHsaEligible,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.medicalIsHsaEligible
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.medicalIsHsaEligible
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.medicalIsHsaEligible
              )}
              selectOptions={[...YES_NO_SELECT_OPTIONS, NULL_SELECT_OPTION]}
              filterBy={EmployeesReportColumn.medicalIsHsaEligible}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.medicalIsHsaEligible),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.medicalIsHsaEligible, record),
      },
      {
        accessor: EmployeesReportColumn.medicalSelfEnroll,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.medicalSelfEnroll
        ),
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.medicalSelfEnroll
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.medicalSelfEnroll
              )}
              selectOptions={[
                ...SELF_ENROLL_SELECT_OPTIONS,
                NULL_SELECT_OPTION,
              ]}
              filterBy={EmployeesReportColumn.medicalSelfEnroll}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.medicalSelfEnroll),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.medicalSelfEnroll, record),
      },
      {
        accessor: EmployeesReportColumn.paymentMethodType,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.paymentMethodType
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.paymentMethodType
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.paymentMethodType
              )}
              selectOptions={[
                ...PAYMENT_METHOD_TYPE_SELECT_OPTIONS,
                NULL_SELECT_OPTION,
              ]}
              filterBy={EmployeesReportColumn.paymentMethodType}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.paymentMethodType),
        render: ({
          paymentMethodType,
          medicalPremium,
          medicalSelfPayAmount,
        }: EmployeesReportRowDto) =>
          paymentMethodType ? (
            <PaymentMethodBadge
              paymentMethod={EmployeePaymentMethodType.ZORRO_PAY}
              premium={medicalPremium || 0}
              selfPayAmount={medicalSelfPayAmount || 0}
            />
          ) : (
            '-'
          ),
      },
      {
        accessor: EmployeesReportColumn.qleOccurredOn,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.qleOccurredOn
        ),
        width: '240px',
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.qleType
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(EmployeesReportColumn.qleType)}
              selectOptions={[...QLE_TYPE_SELECT_OPTIONS, NULL_SELECT_OPTION]}
              filterBy={EmployeesReportColumn.qleType}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.qleOccurredOn),
        render: ({
          qleId,
          qleType,
          qleOccurredOn,
          qleOtherReason,
        }: EmployeesReportRowDto) =>
          qleId && qleType && qleOccurredOn ? (
            <QualifyingLifeEventLabel
              qualifyingLifeEvent={{
                type: qleType,
                occurredOn: qleOccurredOn || '',
                otherReason: qleOtherReason,
              }}
              shouldShowTitle={false}
            />
          ) : (
            '-'
          ),
      },
      {
        accessor: EmployeesReportColumn.enrollmentTeamName,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.enrollmentTeamName
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.enrollmentTeamId
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.enrollmentTeamName
              )}
              selectOptions={getFilterOptionsForColumn(
                EmployeesReportColumn.enrollmentTeamName
              )}
              filterBy={EmployeesReportColumn.enrollmentTeamId}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.enrollmentTeamName),
        render: (record: EmployeesReportRowDto) =>
          renderTextCellValue(EmployeesReportColumn.enrollmentTeamName, record),
      },
      {
        accessor: EmployeesReportColumn.enrollmentAgentName,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.enrollmentAgentName
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.enrollmentAgentEmail
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.enrollmentAgentName
              )}
              selectOptions={getFilterOptionsForColumn(
                EmployeesReportColumn.enrollmentAgentName
              )}
              filterBy={EmployeesReportColumn.enrollmentAgentEmail}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.enrollmentAgentName),
        render: ({
          enrollmentAgentFirstName,
          enrollmentAgentLastName,
          enrollmentAgentName,
          enrollmentAgentEmail,
        }: EmployeesReportRowDto) => {
          return enrollmentAgentEmail ? (
            <Group gap="xs" wrap="nowrap">
              <Avatar size="sm" color="indigo">
                {getInitials(
                  enrollmentAgentFirstName || '',
                  enrollmentAgentLastName || ''
                )}
              </Avatar>
              <Tooltip label={enrollmentAgentEmail} maw={500} position="top">
                <Text
                  size="sm"
                  style={{
                    ...singleLineTruncatedTextWithEllipsis,
                    maxWidth: 114,
                  }}
                >
                  {enrollmentAgentName}
                </Text>
              </Tooltip>
            </Group>
          ) : (
            '-'
          );
        },
      },
      {
        accessor: EmployeesReportColumn.employeeRole,
        title: getEmployeeReportColumnTitle(EmployeesReportColumn.employeeRole),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.employeeRole
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.employeeRole
              )}
              selectOptions={EMPLOYEE_ROLE_SELECT_OPTIONS}
              setFilters={handleFilterChange}
              filters={filters}
              filterBy={EmployeesReportColumn.employeeRole}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.employeeRole),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.employeeRole, record),
      },
      {
        accessor: EmployeesReportColumn.ssn,
        width: '10rem',
        title: getEmployeeReportColumnTitle(EmployeesReportColumn.ssn),
        hidden: shouldHideColumn(EmployeesReportColumn.ssn),
        render: (record: EmployeesReportRowDto) => (
          <EmployeeSsnCell ssn={record.ssn} employeeId={record.employeeId} />
        ),
      },
      {
        accessor: EmployeesReportColumn.employmentType,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.employmentType
        ),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.employmentType
        ),
        filter: (
          <SelectFilter
            title={getColumnTitleForFilter(
              EmployeesReportColumn.employmentType
            )}
            selectOptions={[
              ...EMPLOYMENT_TYPE_SELECT_OPTIONS,
              NULL_SELECT_OPTION,
            ]}
            filterBy={EmployeesReportColumn.employmentType}
            setFilters={handleFilterChange}
            filters={filters}
          />
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.employmentType),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.employmentType, record),
      },
      {
        accessor: EmployeesReportColumn.hireDate,
        title: getEmployeeReportColumnTitle(EmployeesReportColumn.hireDate),
        sortable: true,
        hidden: shouldHideColumn(EmployeesReportColumn.hireDate),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.hireDate, record),
      },
      {
        accessor: EmployeesReportColumn.employeeTerminationDate,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.employeeTerminationDate
        ),
        sortable: true,
        hidden: shouldHideColumn(EmployeesReportColumn.employeeTerminationDate),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(
            EmployeesReportColumn.employeeTerminationDate,
            record
          ),
      },
      {
        accessor: EmployeesReportColumn.state,
        title: getEmployeeReportColumnTitle(EmployeesReportColumn.state),
        sortable: true,
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.state
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(EmployeesReportColumn.state)}
              selectOptions={getFilterOptionsForColumn(
                EmployeesReportColumn.state
              )}
              filterBy={EmployeesReportColumn.state}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.state),
        render: (record: EmployeesReportRowDto) =>
          renderCellValue(EmployeesReportColumn.state, record),
      },
      {
        accessor: EmployeesReportColumn.subEntityName,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.subEntityName
        ),
        width: '20.125rem',
        sortable: true,
        cellsStyle: () => ({ padding: '0.75rem' }),
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.subEntityName
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.subEntityName
              )}
              selectOptions={getFilterOptionsForColumn(
                EmployeesReportColumn.subEntityName
              )}
              filterBy={EmployeesReportColumn.subEntityName}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.subEntityName),
        render: (record: EmployeesReportRowDto) =>
          renderTextCellValue(EmployeesReportColumn.subEntityName, record),
      },
      {
        accessor: EmployeesReportColumn.subEntityName2,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.subEntityName2
        ),
        width: '20.125rem',
        sortable: true,
        cellsStyle: () => ({ padding: '0.75rem' }),
        filtering: filters.some(
          (filter) => filter.type === EmployeesReportColumn.subEntityName2
        ),
        filter: (
          <Group p="xs">
            <SelectFilter
              title={getColumnTitleForFilter(
                EmployeesReportColumn.subEntityName2
              )}
              selectOptions={getFilterOptionsForColumn(
                EmployeesReportColumn.subEntityName2
              )}
              filterBy={EmployeesReportColumn.subEntityName2}
              setFilters={handleFilterChange}
              filters={filters}
            />
          </Group>
        ),
        hidden: shouldHideColumn(EmployeesReportColumn.subEntityName2),
        render: (record: EmployeesReportRowDto) =>
          renderTextCellValue(EmployeesReportColumn.subEntityName2, record),
      },
      {
        accessor: EmployeesReportColumn.employeeIdFromEmployer,
        title: getEmployeeReportColumnTitle(
          EmployeesReportColumn.employeeIdFromEmployer
        ),
        width: '20.125rem',
        sortable: true,
        cellsStyle: () => ({ padding: '0.75rem' }),
        hidden: shouldHideColumn(EmployeesReportColumn.employeeIdFromEmployer),
        render: (record: EmployeesReportRowDto) =>
          renderTextCellValue(
            EmployeesReportColumn.employeeIdFromEmployer,
            record
          ),
      },
      {
        accessor: EmployeesReportColumn.employeeId,
        title: getEmployeeReportColumnTitle(EmployeesReportColumn.employeeId),
        width: '8rem',
        sortable: true,
        hidden: shouldHideColumn(EmployeesReportColumn.employeeId),
        render: (record: EmployeesReportRowDto) =>
          renderTextCellValue(EmployeesReportColumn.employeeId, record),
      },
    ],
    [
      pageRecords,
      impersonatedEmployeeId,
      employer.id,
      isDon,
      filters,
      filterOptions,
    ]
  );

  const createPaginationText = ({
    from,
    to,
    totalRecords,
  }: {
    from: number;
    to: number;
    totalRecords: number;
  }) => (
    <PaginationSelect
      from={from}
      to={to}
      totalRecords={totalRecords}
      handlePageSizeChange={handlePageSizeChange}
      pageSize={pageSize}
    />
  );

  const hasNoEmployees =
    !isLoading && !meta?.total && filters.length === 0 && !searchValue;

  if (hasNoEmployees) {
    return hasAllowanceModel ? (
      <NoEmployeesEmptyState setOverlayId={setOverlayId} />
    ) : (
      <AllowanceModelPendingEmptyState employerId={employer.id} />
    );
  }

  return (
    <>
      <SearchableDataTable
        idAccessor="employeeId"
        heading={
          <PlanYearTitle
            employerId={employer.id}
            titlePrefix="Employees"
            onSelect={(period) => {
              void sendEvent('select_year_dropdown', {
                from_year: period.effectiveFrom,
                to_year: period.effectiveUntil,
              });
              void setOpenEnrollmentPeriodQuery(period.id);
              setEnrollmentPeriodLocal(period.id);
            }}
          />
        }
        componentsLeftToDownloadButton={
          <Group gap="md">
            <ActionsMenu
              setOverlayId={setOverlayId}
              recordsForActions={employeesForActions}
              employerId={employer.id}
            />
            <EmployeeManagementActionsMenu
              setOverlayId={setOverlayId}
              isDisabled={isLoading}
            />
          </Group>
        }
        isLoading={isLoading}
        records={isLoading ? undefined : pageRecords}
        totalRecords={meta?.total || 0}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={handlePageChange}
        onDownload={handleDownload}
        isSearchEnabled
        searchValue={searchValue}
        onSearchChange={handleSearchValueChange}
        sortStatus={sortStatus}
        onSortStatusChange={handleSortStatusChange}
        filtersCount={filters.length}
        clearFilters={handleClearFilters}
        availableViews={availableViews}
        currentView={view}
        setCurrentView={handleCurrentView}
        isSelectable={isSelectable}
        selectedRecords={selectedEmployees}
        onSelectedRecordsChange={(selectedRecords: EmployeesReportRowDto[]) =>
          setSelectedEmployeeIds?.(
            selectedRecords.map((record) => record.employeeId)
          )
        }
        clearSelectedRecords={() => setSelectedEmployeeIds?.([])}
        shouldPinFirstColumn
        shouldGrow
        highlightOnHover
        columns={columns}
        paginationText={createPaginationText}
      />
      <LoadingOverlay visible={isDownloadLoading} />
    </>
  );
};
